import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, Package, GraduationCap, Printer, Check, ShoppingCart, Sparkles, ArrowLeft, Tag, Gift, Flame, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_ICONS = {
  ebook: Book,
  appendices: Package,
  curriculum: GraduationCap,
  print: Printer,
};

const PRODUCT_COLORS = {
  ebook: { bg: 'from-amber-900/60 to-amber-800/40', border: 'border-amber-600/40', accent: 'text-amber-400', check: 'bg-amber-600' },
  appendices: { bg: 'from-teal-900/60 to-teal-800/40', border: 'border-teal-600/40', accent: 'text-teal-400', check: 'bg-teal-600' },
  curriculum: { bg: 'from-purple-900/60 to-purple-800/40', border: 'border-purple-600/40', accent: 'text-purple-400', check: 'bg-purple-600' },
  print: { bg: 'from-slate-800/60 to-slate-700/40', border: 'border-slate-500/40', accent: 'text-slate-300', check: 'bg-slate-600' },
};

const BookPurchase = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [products, setProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [discountInfo, setDiscountInfo] = useState({ percent: 0, pieces: 0, reading: 0 });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API}/api/book-purchase/products`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
        setDiscountInfo({
          percent: data.user_discount_percent || 0,
          pieces: data.puzzle_pieces_studied || 0,
          reading: data.reading_progress || 0,
        });
        // Auto-select ebook
        const autoSelect = {};
        (data.products || []).forEach(p => {
          if (p.product_key === 'ebook' && p.available) autoSelect[p.product_key] = true;
        });
        setSelectedItems(autoSelect);
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    }
    setLoading(false);
  };

  const toggleItem = (key) => {
    setSelectedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getSelectedProducts = () => products.filter(p => selectedItems[p.product_key]);

  const getTotal = () => {
    return getSelectedProducts().reduce((sum, p) => sum + (p.discounted_price || p.base_price), 0);
  };

  const getSubtotal = () => {
    return getSelectedProducts().reduce((sum, p) => sum + p.base_price, 0);
  };

  const getSavings = () => {
    return Math.max(getSubtotal() - getTotal(), 0);
  };

  const handleCheckout = async () => {
    if (!isAuthenticated()) {
      toast.error('Please sign in to purchase');
      navigate('/login');
      return;
    }
    const selected = getSelectedProducts();
    if (selected.length === 0) {
      toast.error('Please select at least one item');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API}/api/book-purchase/checkout`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({
          items: selected.map(p => ({ product_key: p.product_key, quantity: 1 })),
          user_email: user?.email || '',
          user_name: user?.name || '',
          origin_url: window.location.origin,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Checkout failed');
      }
    } catch (err) {
      toast.error('Error processing checkout');
    }
    setProcessing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0806]">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-amber-600/50 font-serif animate-pulse">Loading...</div>
        </div>
      </div>
    );
  }

  const selectedCount = Object.values(selectedItems).filter(Boolean).length;

  // Bundle deal logic
  const BUNDLE_DISCOUNT = 15; // 15% additional discount when buying 3+ items
  const bundleApplies = selectedCount >= 3;

  const getRetailTotal = () => {
    return getSelectedProducts().reduce((sum, p) => sum + (p.retail_price || p.base_price), 0);
  };

  const getCurriculumSavings = () => {
    return getSelectedProducts().reduce((sum, p) => {
      if (p.discount_percent > 0) return sum + (p.base_price - p.discounted_price);
      return sum;
    }, 0);
  };

  const getBundleSavings = () => {
    if (!bundleApplies) return 0;
    const afterDiscount = getTotal();
    return afterDiscount * (BUNDLE_DISCOUNT / 100);
  };

  const getFinalTotal = () => {
    return getTotal() - getBundleSavings();
  };

  const getTotalSavings = () => {
    return getRetailTotal() - getFinalTotal();
  };

  return (
    <div className="min-h-screen bg-[#0a0806] text-amber-100" data-testid="book-purchase-page">
      <Header />

      {/* Hero */}
      <div className="relative py-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-900/10 via-transparent to-transparent" />
        <div className="container mx-auto px-4 relative">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-amber-600/60 hover:text-amber-400 mb-6 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>

          <div className="max-w-3xl">
            <p className="text-[10px] tracking-[0.4em] uppercase font-mono text-amber-600/50 mb-2">The King's Return Collection</p>
            <h1 className="text-3xl sm:text-4xl font-serif text-amber-100 mb-3">Complete Your Library</h1>
            <p className="text-amber-500/60 font-serif text-sm max-w-xl">
              Select the items you'd like to add to your collection. Your study progress earns you discounts.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pb-20">
        <div className="flex flex-col lg:flex-row gap-8 max-w-6xl mx-auto">

          {/* Product Cards */}
          <div className="flex-1 space-y-4">
            {/* Bundle Deal Banner */}
            <div className={`flex items-center gap-3 px-5 py-3 rounded-xl border transition-all ${
              bundleApplies
                ? 'bg-gradient-to-r from-orange-900/40 to-amber-800/20 border-orange-600/40'
                : 'bg-[#12100d] border-amber-900/20'
            }`} data-testid="bundle-deal-banner">
              <Flame className={`h-5 w-5 shrink-0 ${bundleApplies ? 'text-orange-400' : 'text-amber-700/40'}`} />
              <div>
                <p className={`text-sm font-medium ${bundleApplies ? 'text-orange-300' : 'text-amber-700/50'}`}>
                  {bundleApplies ? `Bundle Deal Active: Extra ${BUNDLE_DISCOUNT}% Off!` : `Select 3+ items for an extra ${BUNDLE_DISCOUNT}% Bundle Discount`}
                </p>
                <p className="text-xs text-amber-600/40">The more you bundle, the more you save</p>
              </div>
            </div>

            {/* Discount Banner */}
            {discountInfo.percent > 0 && (
              <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-gradient-to-r from-emerald-900/40 to-emerald-800/20 border border-emerald-700/30" data-testid="discount-banner">
                <Sparkles className="h-5 w-5 text-emerald-400 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-emerald-300">{discountInfo.percent}% Discount Earned</p>
                  <p className="text-xs text-emerald-500/70">
                    {discountInfo.pieces} puzzle pieces studied + {Math.round(discountInfo.reading)}% reading progress
                  </p>
                </div>
              </div>
            )}

            {products.map(product => {
              const Icon = PRODUCT_ICONS[product.category] || Book;
              const colors = PRODUCT_COLORS[product.category] || PRODUCT_COLORS.ebook;
              const isSelected = selectedItems[product.product_key];
              const isAvailable = product.available || product.preorder;

              return (
                <div
                  key={product.product_key}
                  onClick={() => isAvailable && toggleItem(product.product_key)}
                  data-testid={`product-card-${product.product_key}`}
                  className={`relative cursor-pointer rounded-xl border p-5 transition-all duration-300 ${
                    !isAvailable ? 'opacity-40 cursor-not-allowed' :
                    isSelected
                      ? `bg-gradient-to-r ${colors.bg} ${colors.border} border-2 shadow-lg`
                      : 'bg-[#12100d] border-amber-900/20 hover:border-amber-700/40'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <div className={`mt-1 w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${
                      isSelected ? `${colors.check} border-transparent` : 'border-amber-800/30 bg-transparent'
                    }`}>
                      {isSelected && <Check className="h-4 w-4 text-white" />}
                    </div>

                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${
                      isSelected ? 'bg-white/10' : 'bg-amber-900/20'
                    }`}>
                      <Icon className={`h-6 w-6 ${isSelected ? colors.accent : 'text-amber-600/50'}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-serif text-base text-amber-100">{product.title}</h3>
                          <p className="text-xs text-amber-600/50 mt-0.5">{product.description}</p>
                        </div>
                        <div className="text-right shrink-0">
                          {/* Retail value */}
                          {product.retail_price && product.retail_price > product.base_price && (
                            <p className="text-[10px] text-rose-400/60 line-through">Retail: ${product.retail_price.toFixed(2)}</p>
                          )}
                          {product.discount_percent > 0 ? (
                            <>
                              <p className="text-xs text-amber-700/50 line-through">${product.base_price.toFixed(2)}</p>
                              <p className="text-lg font-mono font-bold text-emerald-400">${product.discounted_price.toFixed(2)}</p>
                              {product.retail_savings > 0 && (
                                <p className="text-[10px] text-emerald-500/70">Save ${product.retail_savings.toFixed(2)}</p>
                              )}
                            </>
                          ) : (
                            <>
                              <p className="text-lg font-mono font-bold text-amber-200">${product.base_price.toFixed(2)}</p>
                              {product.retail_price && product.retail_price > product.base_price && (
                                <p className="text-[10px] text-emerald-500/70">Save ${(product.retail_price - product.base_price).toFixed(2)}</p>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Tags */}
                      <div className="flex gap-2 mt-2">
                        {product.preorder && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-900/40 text-blue-300 border border-blue-700/30 font-mono uppercase tracking-wider">
                            Pre-order
                          </span>
                        )}
                        {product.discount_percent > 0 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-900/40 text-emerald-300 border border-emerald-700/30 font-mono uppercase tracking-wider flex items-center gap-1">
                            <Tag className="h-2.5 w-2.5" /> {product.discount_percent}% off
                          </span>
                        )}
                        {!product.available && !product.preorder && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-900/40 text-red-300 border border-red-700/30 font-mono uppercase tracking-wider">
                            Coming Soon
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:w-80 shrink-0">
            <div className="sticky top-24 rounded-xl border border-amber-900/30 bg-[#12100d] p-6" data-testid="order-summary">
              <h3 className="text-sm font-mono uppercase tracking-wider text-amber-600/60 mb-4">Order Summary</h3>

              {selectedCount === 0 ? (
                <p className="text-sm text-amber-700/40 font-serif italic">No items selected</p>
              ) : (
                <div className="space-y-3">
                  {getSelectedProducts().map(p => (
                    <div key={p.product_key} className="flex justify-between text-sm">
                      <span className="text-amber-300/80 font-serif truncate pr-2">{p.title}</span>
                      <div className="text-right shrink-0">
                        {p.discount_percent > 0 && (
                          <span className="text-xs text-amber-700/50 line-through mr-2">${p.base_price.toFixed(2)}</span>
                        )}
                        <span className="text-amber-100 font-mono">${(p.discounted_price || p.base_price).toFixed(2)}</span>
                      </div>
                    </div>
                  ))}

                  {/* Retail Value */}
                  <div className="border-t border-amber-900/20 pt-3 flex justify-between text-sm">
                    <span className="text-amber-700/50">Retail Value</span>
                    <span className="text-amber-700/50 font-mono">${getRetailTotal().toFixed(2)}</span>
                  </div>

                  {/* Curriculum Discount */}
                  {getCurriculumSavings() > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-emerald-500/80 flex items-center gap-1"><Star className="h-3 w-3" /> Study Discount ({discountInfo.percent}%)</span>
                      <span className="text-emerald-400 font-mono">-${getCurriculumSavings().toFixed(2)}</span>
                    </div>
                  )}

                  {/* Bundle Discount */}
                  {bundleApplies && (
                    <div className="flex justify-between text-sm">
                      <span className="text-orange-400 flex items-center gap-1"><Flame className="h-3 w-3" /> Bundle Discount ({BUNDLE_DISCOUNT}%)</span>
                      <span className="text-orange-400 font-mono">-${getBundleSavings().toFixed(2)}</span>
                    </div>
                  )}

                  {/* Total Savings */}
                  {getTotalSavings() > 0.01 && (
                    <div className="flex justify-between text-sm bg-emerald-900/20 -mx-2 px-2 py-1.5 rounded-lg">
                      <span className="text-emerald-300 font-medium flex items-center gap-1"><Gift className="h-3 w-3" /> Total Savings</span>
                      <span className="text-emerald-300 font-mono font-bold">-${getTotalSavings().toFixed(2)}</span>
                    </div>
                  )}

                  <div className="border-t border-amber-700/30 pt-3 flex justify-between">
                    <span className="text-amber-200 font-medium">You Pay</span>
                    <span className="text-xl font-mono font-bold text-amber-100">${getFinalTotal().toFixed(2)}</span>
                  </div>
                </div>
              )}

              <Button
                onClick={handleCheckout}
                disabled={selectedCount === 0 || processing}
                data-testid="checkout-btn"
                className="w-full mt-6 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-medium py-3 rounded-lg transition-all disabled:opacity-40"
              >
                {processing ? (
                  <span className="animate-pulse">Processing...</span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <ShoppingCart className="h-4 w-4" />
                    {selectedCount > 0 ? `Pay $${getFinalTotal().toFixed(2)}` : 'Select Items'}
                  </span>
                )}
              </Button>

              {!isAuthenticated() && (
                <p className="text-xs text-amber-700/50 text-center mt-3">Sign in to apply your earned discount</p>
              )}

              <div className="mt-6 pt-4 border-t border-amber-900/20 space-y-2">
                <p className="text-[10px] text-amber-700/40 font-mono uppercase tracking-wider">How Discounts Work</p>
                <div className="text-xs text-amber-600/50 space-y-1">
                  <p>+2% per puzzle piece studied (up to 50%)</p>
                  <p>+1% per 10% reading progress</p>
                  <p>+{BUNDLE_DISCOUNT}% extra when buying 3+ items</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookPurchase;
